import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import { ChevronLeftIcon } from '@repo/ui/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { getHostFamily } from '../../../api/host-families/get-host-family'
import { updateHostFamily } from '../../../api/host-families/update-host-family'
import { ItemNotFound } from '../../../components/elements/item-not-found'
import LoadingScreen from '../../../components/elements/loading/loading-screen'
import UserNavBar from '../../../components/elements/nav-bar'
import HostFamilyForm from '../../../components/pages/host-families/host-family-form'
import HostFamilyLecComments from '../../../components/pages/host-families/host-family-lec-comments'
import HostFamilyOlCommentTable from '../../../components/pages/host-families/host-family-ol-comments-table'
import { Paths } from '../../../lib/constants/path'
import useAuth from '../../../lib/hooks/use-auth'
import { OLTypeEnum } from '../../../lib/types/enums/ol/ol-type.enum'
import { updateButtonsConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import NotFoundPage from '../../common/not-found'
import { mapHostFamilyInputToRequest, mapHostFamilyToInput } from './utils'

const HostFamilyUpdatePage = () => {
  const { id } = useParams<{ id: string }>()
  const { navigate, back } = useNavigator()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  // the authenticated user already carries its own prestataire, so no extra fetch is needed
  const showLocalRepresentative =
    user?.ol?.type === OLTypeEnum.PARTNER && !!user.ol.isOtoIndividual

  const { data: hostFamily, isPending } = useQuery({
    queryKey: ['host-family', id],
    queryFn: () => {
      if (id) return getHostFamily(+id)
    },
    enabled: !!id
  })

  const { mutateAsync } = useMutation({
    mutationFn: updateHostFamily,
    onError: showAxiosError,
    onSuccess() {
      queryClient.invalidateQueries({ exact: true, queryKey: ['host-family', id] })
      toast.success('Host family updated successfully')
    }
  })

  if (!id || isNaN(+id)) return <ItemNotFound title="Host family not found" />
  if (isPending) return <LoadingScreen />
  if (!hostFamily) return <NotFoundPage />

  return (
    <div>
      <UserNavBar
        title={toFullName(hostFamily.familyName)}
        icon={ChevronLeftIcon}
        onIconClick={() =>
          navigate(Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_LIST_PAGE, { replace: true })
        }
      />
      <div className={'h-4'} />

      <Box>
        <HostFamilyForm
          defaultValues={mapHostFamilyToInput(hostFamily)}
          showLocalRepresentative={showLocalRepresentative}
          hostFamilyId={hostFamily.id}
          buttonsConfig={updateButtonsConfig({
            onCancel: () => back(Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_LIST_PAGE),
            onUpdate: (values) =>
              mutateAsync({ id: hostFamily.id, ...mapHostFamilyInputToRequest(values) }),
            onUpdateThenCreate: async (values) => {
              await mutateAsync({ id: hostFamily.id, ...mapHostFamilyInputToRequest(values) })
              navigate(Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_CREATE_PAGE)
            },
            onUpdateThenList: async (values) => {
              await mutateAsync({ id: hostFamily.id, ...mapHostFamilyInputToRequest(values) })
              navigate(Paths.OL_PERSONAL_SPACE_HOST_FAMILIES_LIST_PAGE)
            },
            isEnglish: true
          })}
        />
      </Box>
      <div className={'h-4'} />
      <Box>
        <HostFamilyOlCommentTable id={Number(id)} />
        <div className={'h-4'} />
        <HostFamilyLecComments id={Number(id)} />
      </Box>
    </div>
  )
}

export default HostFamilyUpdatePage
